/**
 * Passkey client primitives (SPEC-68 §4.10).
 *
 * Thin, typed wrappers — deliberately kept free of React so the three flows are
 * unit-testable with a stubbed `fetch` (this repo has no DOM harness: vitest runs
 * `environment: "node"`, so pure modules + stubbed fetch are the established
 * idiom — see csrf-header.test.ts / webauthn-error.test.ts).
 *
 * `enrollPasskey` and `beginDriverPasskeyLogin` both deliberately return a
 * RESULT OBJECT rather than throwing for expected conditions: the caller (a
 * field worker's screen) must be able to tell "no passkey here, use the PIN"
 * apart from "something broke", and neither is an exception.
 */
import { apiFetch, ApiError } from "./api";
// `executeWebAuthnAuthentication` joins when Phase 10's DriverLogin consumes
// it (the begin → prompt → finish login flow); importing it now would be an
// unused-symbol violation. See lib/webauthn.ts for the real implementation.
import { executeWebAuthnRegistration } from "./webauthn";
import type {
  PasskeyLoginBeginResponse,
  EnrollmentStatus,
} from "@eagledispatch/shared";

/** POST /auth/passkey/login/begin — mint options for a CHOSEN driver. */
export async function beginDriverPasskeyLogin(
  userId: string,
  tagUid?: string,
): Promise<{ available: boolean; challengeToken?: string; options?: unknown }> {
  return await apiFetch<PasskeyLoginBeginResponse>(
    "/api/v1/auth/passkey/login/begin",
    {
      method: "POST",
      body: JSON.stringify({
        userId,
        // SPEC-70 §2.7 — the presence factor. Omitted entirely when no scan
        // produced a UID, keeping the pre-gate wire shape byte-identical.
        ...(tagUid ? { tagUid } : {}),
      }),
    },
  );
}

export type EnrollResult =
  | { ok: true }
  | { ok: false; code: string; message: string };

/**
 * Run the full enrollment ceremony: begin -> platform prompt -> finish.
 * `code` carries the server's machine-readable error so the caller can treat
 * `enrollment_not_available` as "just hide the card" rather than an error.
 */
export async function enrollPasskey(label?: string): Promise<EnrollResult> {
  try {
    const begin = await apiFetch<{
      options: Record<string, unknown>;
      challengeToken: string;
    }>("/api/v1/webauthn/register/begin", { method: "POST" });

    const response = await executeWebAuthnRegistration(begin.options as never);

    await apiFetch<{ verified: boolean }>("/api/v1/webauthn/register/finish", {
      method: "POST",
      body: JSON.stringify({
        challengeToken: begin.challengeToken,
        response,
        ...(label ? { label } : {}),
      }),
    });

    return { ok: true };
  } catch (err) {
    if (err instanceof ApiError) {
      return { ok: false, code: err.code, message: err.message };
    }
    // A user-cancelled platform prompt arrives as a DOMException, not an
    // ApiError. Surface it by name so the caller can retry silently.
    const name =
      err && typeof err === "object" && "name" in err
        ? String((err as { name: unknown }).name)
        : "error";
    return {
      ok: false,
      code: name,
      message: err instanceof Error ? err.message : String(err),
    };
  }
}

export type PasskeyStatus = {
  hasPasskey: boolean;
  passkeysRequired: boolean;
  canEnroll: boolean;
  reason: string | null;
};

/**
 * GET /webauthn/credentials — the caller's own status. `enrollment` is null for
 * OFFICE (no presence gate applies to that role), which maps to
 * `canEnroll:false, reason:null` so the card hides itself.
 */
export async function fetchPasskeyStatus(): Promise<PasskeyStatus> {
  const body = await apiFetch<{
    credentials: { id: string }[];
    passkeysRequired: boolean;
    enrollment: EnrollmentStatus | null;
  }>("/api/v1/webauthn/credentials");

  return {
    hasPasskey: body.credentials.length > 0,
    passkeysRequired: body.passkeysRequired,
    canEnroll: body.enrollment?.canEnroll ?? false,
    reason: body.enrollment?.reason ?? null,
  };
}
